import { useEffect, useRef } from 'react';
import config from '@/config';
import { registerAmplitudeEnrichmentPlugins } from '@/utilities/logger/amplitude/amplitudeEventTransforms';
import { trackUserAction } from './tracking/trackUserAction';
import { TRACK_USER_ACTION_EVENT } from './tracking/constants';

/** localStorage key used for experiment variant stickiness (persist across sessions) */
export const AMP_EXP_STICKINESS_KEY = 'amp_exp_stickiness';

/** How long stored stickiness variants stay valid before they must be refetched (localStorage has no native expiry). */
export const AMP_EXP_STICKINESS_TTL_MS = 365 * 24 * 60 * 60 * 1000;

type StickinessVariants = Record<string, { key?: string; value?: string }>;

/** Stored shape: variants plus an absolute expiry so we can age them out ourselves. */
export type StickinessEnvelope = {
  variants: StickinessVariants;
  expiresAt: number; // epoch ms
};

const getStickinessVariantsFromStorage = (): StickinessVariants | undefined => {
  if (typeof localStorage === 'undefined') return undefined;
  try {
    const raw = localStorage.getItem(AMP_EXP_STICKINESS_KEY);
    if (!raw) return undefined;
    const { variants, expiresAt } = (JSON.parse(raw) ??
      {}) as Partial<StickinessEnvelope>;
    // Drop legacy/invalid entries and anything past its expiry, so the next
    // init refetches and the writer replaces it with a fresh, re-stamped value.
    if (
      !variants ||
      typeof variants !== 'object' ||
      typeof expiresAt !== 'number' ||
      Date.now() > expiresAt
    ) {
      localStorage.removeItem(AMP_EXP_STICKINESS_KEY);
      return undefined;
    }
    return variants;
  } catch {
    // ignore parse / access errors (e.g. private mode, blocked storage)
    return undefined;
  }
};

const stickinessVariants = getStickinessVariantsFromStorage();
const hasStickinessVariants =
  stickinessVariants !== undefined &&
  Object.keys(stickinessVariants).length > 0;

const defaultOptions = {
  analytics: {
    defaultTracking: {
      attribution: {
        resetSessionOnNewCampaign: true,
      },
      pageViews: false,
      sessions: false,
      fileDownloads: false,
      formInteractions: false,
    },
    identityStorage: 'localStorage' as const,
    minIdLength: 1,
  },
  experiment: {
    /** Fetch on start when no stored variants; when present, defer fetch and use local eval first */
    fetchOnStart: !hasStickinessVariants,
    /** Load flag configs for local evaluation on start */
    pollOnStart: true,
    fetchTimeoutMillis: 500,
    fetchRetries: 1,
    fetchRetryBackoffMinMillis: 0,
    fetchRetryTimeoutMillis: 500,
    /** Stickiness: bootstrap from localStorage so variants persist across sessions */
    initialVariants: stickinessVariants,
  },
};

const apiKey = config?.amplitude?.apiKey;

/** Promise of the Amplitude unified module, set once init runs. Resolves after initAll() completes. */
let unifiedModulePromise: Promise<typeof import('@amplitude/unified')> | null =
  null;

const ensureInit = (): void => {
  if (didInitRef.current) return;
  didInitRef.current = true;
  unifiedModulePromise = import('@amplitude/unified').then(async (m) => {
    await m.initAll(apiKey, defaultOptions);
    registerAmplitudeEnrichmentPlugins(m.add.bind(m));
    return m;
  });
};

const didInitRef = { current: false };
const didFireScreenLoadRef = { current: false };

/**
 * Returns the Amplitude unified module after init has completed. If init has not run yet
 * (e.g. idle callback not fired), this triggers init so the module loads. Use this instead
 * of static import so the SDK (and its 8+ cdn.amplitude.com chunks) are not in the main bundle.
 */
export const getUnifiedModule = (): Promise<
  typeof import('@amplitude/unified')
> => {
  if (!unifiedModulePromise) ensureInit();
  return (
    unifiedModulePromise ??
    Promise.reject(new Error('Amplitude not initialised'))
  );
};

/**
 * Initializes Amplitude after first paint so the 9+ requests (gs.amplitude.com + 8 chunks
 * from cdn.amplitude.com over HTTP/1.1) do not block FCP. The SDK is lazy-loaded so it is
 * not in the main bundle. Uses requestIdleCallback when available, else setTimeout(0).
 */
const fireScreenLoadEvent = () => {
  if (didFireScreenLoadRef.current) return;
  didFireScreenLoadRef.current = true;
  trackUserAction({ event: TRACK_USER_ACTION_EVENT.SCREEN_LOAD });
};

export const useInitAmpli = () => {
  const didInit = useRef(false);

  useEffect(() => {
    if (!apiKey || didInit.current) return;
    const run = () => {
      if (didInit.current) return;
      didInit.current = true;
      ensureInit();
      fireScreenLoadEvent();
    };
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(run, { timeout: 2000 });
    } else {
      setTimeout(run, 0);
    }
  }, []);
};

export default useInitAmpli;
